import { Box, Heading, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { dateFormatter, durationInMinutes } from 'app/helpers/format-helper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ZoomSyncResult } from './use-zoom-attendance-sync';

type Props = {
  result: ZoomSyncResult;
};

// Lists the Zoom participants that matched no invited learner (dial-ins, or a
// different email than the one on the roster) so the facilitator can credit
// them by hand.
export const UnmatchedZoomParticipants: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation();
  const { matchedCount, unmatchedParticipants } = result;

  if (matchedCount === 0 && unmatchedParticipants.length === 0) {
    return (
      <Text mt={8} color="gray.700">
        {t('connect.zoomSync.noParticipants')}
      </Text>
    );
  }

  if (unmatchedParticipants.length === 0) {
    return null;
  }

  return (
    <Box mt={10}>
      <Heading size="h4" as="h3" mb={2}>
        {t('connect.zoomSync.unmatchedTitle')}
      </Heading>
      <Text mb={4} color="gray.700">
        {t('connect.zoomSync.unmatchedDescription')}
      </Text>
      <Table size="sm" backgroundColor="white" data-testid="unmatched-zoom-participants">
        <Thead>
          <Tr>
            <Th>{t('connect.meetingUserTableNameHeader')}</Th>
            <Th>{t('connect.zoomSync.email')}</Th>
            <Th>{t('connect.zoomSync.joinTime')}</Th>
            <Th>{t('connect.zoomSync.leaveTime')}</Th>
            <Th>{t('connect.zoomSync.duration')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {unmatchedParticipants.map((participant, index) => (
            // Zoom can report the same person more than once, so nothing on a
            // participant is unique on its own.
            <Tr key={`${participant.name}-${participant.joinTime}-${index}`}>
              <Td>{participant.name}</Td>
              <Td>{participant.email || t('connect.zoomSync.noEmail')}</Td>
              <Td>{dateFormatter.datetime(participant.joinTime ?? undefined)}</Td>
              <Td>{dateFormatter.datetime(participant.leaveTime ?? undefined)}</Td>
              <Td>{t('connect.zoomSync.minutes', { minutes: durationInMinutes(participant.durationSeconds) })}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
